const { useCallback } = require('react');
const metricsService = require('../browserServices/metrics');

/**
 * Hook for sending metrics to StatsD/Datadog
 * @returns {Object} Metric sending functions
 */
const useMetrics = () => {
  /**
   * Send a timing metric
   * @param {string} metricName - Name of the metric
   * @param {number} duration - Duration in milliseconds
   * @param {Object} options - Additional options (sampleRate, tags)
   */
  const sendTiming = useCallback((metricName, duration, options = {}) => {
    metricsService.timing(metricName, duration, options)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to send timing metric:', error);
      });
  }, []);

  /**
   * Increment a metric counter
   * @param {string} metricName - Name of the metric
   * @param {number} value - Increment value (default: 1)
   * @param {Object} options - Additional options (sampleRate, tags)
   */
  const increment = useCallback((metricName, value = 1, options = {}) => {
    metricsService.increment(metricName, value, options)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to increment metric:', error);
      });
  }, []);

  /**
   * Send a gauge metric
   * @param {string} metricName - Name of the metric
   * @param {number} value - Gauge value
   * @param {Object} options - Additional options (sampleRate, tags)
   */
  const sendGauge = useCallback((metricName, value, options = {}) => {
    metricsService.gauge(metricName, value, options)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to send gauge metric:', error);
      });
  }, []);

  /**
   * Time and send the duration of a function execution
   * @param {string} metricName - Name of the metric
   * @param {Function} fn - Function to time
   * @param {Object} options - Additional options (sampleRate, tags)
   * @returns {Promise} Result of the function
   */
  const timeFunction = useCallback(async (metricName, fn, options = {}) => {
    const start = Date.now();
    try {
      return await fn();
    } finally {
      const duration = Date.now() - start;
      sendTiming(metricName, duration, options);
    }
  }, [sendTiming]);

  return {
    sendTiming,
    increment,
    sendGauge,
    timeFunction,
  };
};

module.exports = useMetrics;
